import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Alert,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import axiosApi from "../Utils/axiosApi";

const ExcelUploadDialog = ({ open, onClose }) => {
  const [hostelId, setHostelId] = useState("");
  const [file, setFile] = useState(null);
  const [hostels, setHostels] = useState([]);
  const [rowErrors, setRowErrors] = useState([]);

  const [banner, setBanner] = useState({
    open: false,
    type: "success",
    message: "",
  });

  /* ===============================
     RESET STATE ON DIALOG OPEN
  ================================ */
  useEffect(() => {
    if (!open) return;

    setHostelId("");
    setFile(null);
    setRowErrors([]);
    setBanner({
      open: false,
      type: "success",
      message: "",
    });
  }, [open]);

  /* ===============================
     LOAD HOSTELS
  ================================ */
  useEffect(() => {
    if (!open) return;

    axiosApi
      .post("/hostel/getHostelName", {})
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.hostels || [];

        setHostels(
          data.map(([id, name]) => ({
            id,
            hostelName: name,
          }))
        );
      })
      .catch(() => setHostels([]));
  }, [open]);

  /* ===============================
     UPLOAD HANDLER
  ================================ */
  const handleSubmit = async () => {
    if (!hostelId) {
      alert("Please select a Hostel.");
      return;
    }
    if (!file) {
      alert("Please upload an Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("hostelId", hostelId);
    formData.append("file", file);

    try {
      await axiosApi.post("/hosteler/bulkUpload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setRowErrors([]);
      setBanner({
        open: true,
        type: "success",
        message: "Hostelers uploaded successfully!",
      });

      setTimeout(() => {
        setHostelId("");
        setFile(null);
        setRowErrors([]);
        setBanner({ open: false, type: "success", message: "" });
        onClose();
      }, 2000);
    } catch (err) {
      const data = err?.response?.data;

      if (Array.isArray(data?.errors)) {
        setRowErrors(data.errors);
        setBanner({
          open: true,
          type: "error",
          message: data.message || "Excel validation failed",
        });
      } else if (Array.isArray(data)) {
        setRowErrors(data);
        setBanner({
          open: true,
          type: "error",
          message: "Excel validation failed",
        });
      } else {
        setRowErrors([]);
        setBanner({
          open: true,
          type: "error",
          message: data?.message || "Upload failed",
        });
      }
    }
  };

  /* ===============================
     CANCEL HANDLER
  ================================ */
  const handleCancel = () => {
    setHostelId("");
    setFile(null);
    setRowErrors([]);
    setBanner({
      open: false,
      type: "success",
      message: "",
    });
    onClose();
  };

  /* ===============================
     UI
  ================================ */
  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 600 }}>
        Upload Hostelers Excel
      </DialogTitle>

      <DialogContent dividers>
        <Collapse in={banner.open}>
          <Alert severity={banner.type} sx={{ mb: 2 }}>
            {banner.message}
          </Alert>
        </Collapse>

        <Grid container spacing={2}>
          {/* Hostel */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Hostel Name</InputLabel>
              <Select
                value={hostelId}
                label="Hostel Name"
                onChange={(e) => setHostelId(e.target.value)}
              >
                {Array.isArray(hostels) &&
                  hostels.map((h) => (
                    <MenuItem key={h.id} value={h.id}>
                      {h.hostelName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* File */}
          <Grid item xs={12}>
            <Button variant="outlined" component="label" fullWidth>
              {file ? file.name : "Choose Excel File"}
              <input
                hidden
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </Button>
          </Grid>

          {/* Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button variant="contained" onClick={handleSubmit}>
                Upload
              </Button>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* ===============================
            ROW ERROR TABLE
        ================================ */}
        {Array.isArray(rowErrors) && rowErrors.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Please fix the following errors and re-upload the file
            </Alert>

            <Paper elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="80">
                      <strong>Row</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Errors</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowErrors.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.row}</TableCell>
                      <TableCell>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {(row.errors || []).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExcelUploadDialog;
